import { useTranslation } from 'react-i18next'
import { PipelineView } from '../PipelineView/PipelineView.tsx'
import { usePipelinesSourceState } from '../data/usePipelinesSource.ts'

/**
 * Gate component for the pipelines feature. Defers mounting `PipelineView`
 * (which assumes a non-null snapshot) until SWR has resolved the four parallel
 * fetches that compose it. Loading / error / auth-required states render in
 * place of the kanban view; the empty-stage state is handled deeper, inside
 * `LeadsTable/empty.tsx`.
 */
export function PipelinesPage() {
  const { snapshot, isLoading, error } = usePipelinesSourceState()
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <PipelineSkeleton
        label={t('pages.pipelines.loading', { defaultValue: 'Loading pipeline…' })}
      />
    )
  }
  if (error) {
    return <PipelineErrorState error={error} />
  }
  if (!snapshot) {
    // Defensive: after isLoading=false and error=null, snapshot is expected to
    // be non-null. Render an inert placeholder rather than throw so the rest
    // of the shell stays interactive.
    return (
      <PipelineSkeleton
        label={t('pages.pipelines.empty', { defaultValue: 'Pipeline data unavailable.' })}
      />
    )
  }
  return <PipelineView />
}

interface SkeletonProps {
  label: string
}

function PipelineSkeleton({ label }: SkeletonProps) {
  return (
    <div className="flex h-full w-full flex-1 items-center justify-center p-8">
      <div role="status" aria-busy="true" className="flex flex-col items-center gap-3 text-muted">
        <div className="h-2 w-32 animate-pulse rounded-full bg-border/40" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em]">{label}</span>
      </div>
    </div>
  )
}

function PipelineErrorState({
  error,
}: {
  error: { kind: string; status: number | null; message: string }
}) {
  const { t } = useTranslation()

  const { title, body } = describeError(error.kind, error.status, t)

  return (
    <div className="flex h-full w-full flex-1 items-center justify-center p-8">
      <div
        role="alert"
        className="bg-gradient-panel-elevated flex max-w-md flex-col gap-2 rounded-lg border border-toast-error-text/30 px-5 py-4 text-foreground shadow-[0_24px_48px_-32px_rgba(0,0,0,0.7)]"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-toast-error-text">
          {title}
        </span>
        <p className="text-sm text-muted">{body}</p>
        {error.status !== null && (
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted/70">
            HTTP {error.status} · {error.message}
          </span>
        )}
      </div>
    </div>
  )
}

function describeError(
  kind: string,
  _status: number | null,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): { title: string; body: string } {
  switch (kind) {
    case 'unauthorized':
      return {
        title: t('pages.pipelines.error.unauthorized.title', {
          defaultValue: 'Authentication required',
        }),
        body: t('pages.pipelines.error.unauthorized.body', {
          defaultValue:
            'The console couldn\'t authenticate against the engine. Set a valid bearer token in localStorage under "ludus.token" and reload.',
        }),
      }
    case 'auth_not_configured':
      return {
        title: t('pages.pipelines.error.authNotConfigured.title', {
          defaultValue: 'Engine misconfigured',
        }),
        body: t('pages.pipelines.error.authNotConfigured.body', {
          defaultValue:
            'The engine is reachable but has no WIDGET_SECRET_TOKEN set. Run `wrangler d1 …` and configure .dev.vars before continuing.',
        }),
      }
    case 'not_found':
      return {
        title: t('pages.pipelines.error.notFound.title', { defaultValue: 'Pipeline not found' }),
        body: t('pages.pipelines.error.notFound.body', {
          defaultValue:
            'The pipeline id in the URL is not present in the connected database. Go back to the pipelines list, or seed via `pnpm db:seed`.',
        }),
      }
    case 'network':
      return {
        title: t('pages.pipelines.error.network.title', { defaultValue: 'Engine unreachable' }),
        body: t('pages.pipelines.error.network.body', {
          defaultValue:
            'Could not reach the engine. Verify that `pnpm -F @ludus/engine dev` is running and VITE_ENGINE_URL points at the right port.',
        }),
      }
    default:
      return {
        title: t('pages.pipelines.error.unknown.title', { defaultValue: 'Request failed' }),
        body: t('pages.pipelines.error.unknown.body', {
          defaultValue:
            'The engine returned an unexpected error. Check the network tab and try again.',
        }),
      }
  }
}
